// src/components/dashboard/DataTable.jsx
import React from 'react';
import { FaSearch, FaBoxOpen } from 'react-icons/fa';

const DataTable = ({
  title,
  subtitle,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  data,
  columns,
  emptyMessage = "No data available",
  emptyIcon: EmptyIcon = FaBoxOpen,
  loading = false,
  gradientFrom = "indigo-600",
  gradientTo = "purple-600",
  onViewAll,
  pagination = null,
  rowClickHandler = null
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className={`bg-gradient-to-r from-${gradientFrom} to-${gradientTo} p-5 text-white`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-xl font-bold">{title}</h3>
            <p className="text-white/80 text-sm mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70" size={16} />
              <input
                type="text"
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2.5 bg-white/20 border border-white/30 rounded-full text-white placeholder-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            {onViewAll && (
              <button
                onClick={onViewAll}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition whitespace-nowrap"
              >
                View All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-auto p-1">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-purple-600"></div>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                {columns.map((col, i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-${col.align || 'left'} text-xs font-semibold text-gray-700 uppercase`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`border-b hover:bg-gray-50 ${rowClickHandler ? 'cursor-pointer' : ''}`}
                    onClick={() => rowClickHandler?.(item)}
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-4 py-4 ${col.align ? `text-${col.align}` : 'text-left'}`}
                      >
                        {col.render ? col.render(item, rowIndex) : (item[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-12 text-gray-500">
                    <EmptyIcon size={48} className="mx-auto mb-3 opacity-30" />
                    <p>{emptyMessage}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && data.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 flex justify-between items-center text-sm border-t">
          <span className="text-gray-600">{pagination.showingText}</span>
          <div className="flex gap-3">
            <button
              onClick={pagination.onPrev}
              disabled={pagination.currentPage === 1}
              className="px-4 py-1.5 bg-white border rounded disabled:opacity-50 hover:bg-gray-100 text-sm"
            >
              Prev
            </button>
            <button
              onClick={pagination.onNext}
              disabled={pagination.currentPage >= pagination.totalPages}
              className="px-4 py-1.5 bg-white border rounded disabled:opacity-50 hover:bg-gray-100 text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;